/**
 * The UK / EUR / US-Women / US-Men size tabs.
 *
 * The active system lives on `<html data-size-system>`, set before first paint by the inline
 * script in `layout/theme.liquid` from localStorage. Size labels rendered by `snippets/size-label.liquid`
 * follow it through CSS alone; only `<option>` text, which cannot hold markup, is relabelled here.
 * The order of SIZE_SYSTEMS matches the label arrays written by `snippets/size-display-input.liquid`.
 */

export const SIZE_SYSTEMS = ['uk', 'eur', 'us_women', 'us_men'];

export const SIZE_SYSTEM_CHANGE_EVENT = 'size-system:change';

const STORAGE_KEY = 'salomon:size-system';

/** @type {Record<string, string>} */
const OPTION_DATASET_KEYS = {
  uk: 'sizeUk',
  eur: 'sizeEur',
  us_women: 'sizeUsWomen',
  us_men: 'sizeUsMen',
};

/** @returns {string} The active size system, UK when none has been chosen. */
export function getSizeSystem() {
  const system = document.documentElement.dataset.sizeSystem;
  return system && SIZE_SYSTEMS.includes(system) ? system : 'uk';
}

/**
 * Switches every size label on the page and remembers the choice for later pages and visits.
 * @param {string | undefined} system
 */
export function setSizeSystem(system) {
  if (!system || !SIZE_SYSTEMS.includes(system)) return;

  document.documentElement.dataset.sizeSystem = system;

  try {
    localStorage.setItem(STORAGE_KEY, system);
  } catch {
    // Storage can be blocked (private mode, disabled site data); the choice then lasts for this page.
  }

  relabelSizeOptions();

  for (const tabs of document.querySelectorAll('size-system-tabs')) {
    if (tabs instanceof SizeSystemTabs) tabs.sync();
  }

  document.dispatchEvent(new CustomEvent(SIZE_SYSTEM_CHANGE_EVENT, { detail: { system } }));
}

/**
 * Relabels `<option>` elements carrying `data-size-uk` / `-eur` / `-us-women` / `-us-men`.
 * `data-size-suffix` is appended as is, e.g. " - Unavailable".
 */
export function relabelSizeOptions() {
  const datasetKey = OPTION_DATASET_KEYS[getSizeSystem()] ?? 'sizeUk';

  for (const option of document.querySelectorAll('option[data-size-uk]')) {
    if (!(option instanceof HTMLOptionElement)) continue;

    const label = option.dataset[datasetKey] || option.dataset.sizeUk || '';
    option.textContent = label + (option.dataset.sizeSuffix ?? '');
  }
}

class SizeSystemTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.sync();
    relabelSizeOptions();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** Reflects the active system in `aria-pressed`. */
  sync() {
    const active = getSizeSystem();

    for (const button of this.querySelectorAll('button[data-size-system]')) {
      if (!(button instanceof HTMLElement)) continue;
      button.setAttribute('aria-pressed', button.dataset.sizeSystem === active ? 'true' : 'false');
    }
  }

  /**
   * Stops the click here: on a product card a bubbled click navigates to the product, and the
   * quick add overlay closes on clicks it does not recognise.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('button[data-size-system]');
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();
    setSizeSystem(button.dataset.sizeSystem);
  };
}

if (!customElements.get('size-system-tabs')) {
  customElements.define('size-system-tabs', SizeSystemTabs);
}
